import { useRemoteOperationsAreaOperatorRole } from '@backoffice-frontend/ad-remote-operations-area'
import {
  FccAdAlertType,
  type FccOperatorRoleType,
} from '@backoffice-frontend/graphql'
import type { UseAlertsForRoleAdAlertFragment } from './useAlertsForRole.hook'

// Tasks that only the Rider Support or Remote Assistance area handles, so the
// AD fleet map and other surfaces without a role don't show them.
const alertsExcludedFromAdFleetMap: FccAdAlertType[] = [
  FccAdAlertType.CabinDeboarding,
]

/**
 * Whether this alert is handled by the given role. Without a role, as on the
 * AD fleet map, every alert is shown except `alertsExcludedFromAdFleetMap`.
 *
 * Prefer useAlertsForRole; reach for this predicate only where a hook cannot be
 * called, such as inside a per-vehicle callback.
 */
export const isAlertVisibleToRole = (
  alert: UseAlertsForRoleAdAlertFragment,
  role: FccOperatorRoleType | undefined,
): boolean =>
  role
    ? alert.visibleTo.includes(role)
    : !alertsExcludedFromAdFleetMap.includes(alert.type)

/**
 * The alerts visible to the current area's operator role, in the order the
 * backend returned them.
 */
export const useAlertsForRole = <A extends UseAlertsForRoleAdAlertFragment>(
  alerts: A[] | undefined,
): A[] => {
  const role = useRemoteOperationsAreaOperatorRole()

  return (alerts ?? []).filter(alert => isAlertVisibleToRole(alert, role))
}
